import Typography from '@mui/material/Typography';
import MeteogrfGraph from './MeteogrfGraph';
import { getAcisData, tsky } from '../js/dataAccessRoutines';
import { variableName } from '../js/constants';

// Meteogram processing //

// Codes for text values
const traceValue = 0.004;
const clmValue = 0.001;
const vrbValue = 0.002;
const obsValue = 'M';   //total sky cover is obscured

const sendResults = (results, title, setResult) => {
  if (results) {
      const code = (
        <MeteogrfGraph 
            options={results.highChartOptions}
            title={title}
            codes={{traceValue, clmValue, vrbValue, obsValue}}
        />
      );
      setResult(code)
  } else {
    setResult("")
  }
}

const summarize = (tsarrs, firstdt, userParams) => {
    const highChartOptions = []
    const grouparrs = []
    // variables that are plotted on the same chart, charts stacked in this order
    const groups = [
        ['temp','dwpt','wetb','htindx','wchill'],
        ['pcpn'],
        ['tsky','rhum'],
        ['wspd','wgust','wdir'],
        ['vis'],
        ['altp','slp','pres']
    ]

    //first hour on x-axis. Time zone is irrelevant since data are already in local standard time; 
    //  using UTC prevents HighCharts/browser from doing DST conversions
    const [yy,mm,dd] = firstdt.slice(0,10).split("-");
    const hh = parseInt(firstdt.slice(11, 13));
    const ptst = Date.UTC(yy, mm-1, dd, hh, 0);

    // match variables to groups
    groups.forEach((gvars, g) => {
        grouparrs.push([])
        gvars.forEach((val) => {
            const vindex = userParams.variables.indexOf(val);
            if (vindex >= 0) {
                 grouparrs[g].push(vindex)
            }
        })
    })

    //set up series for HighCharts
    grouparrs.forEach((garr, g) => {
        if (garr.length > 0) {
            const series = [];
            garr.forEach((vindex) => {
                const v = variableName[userParams.variables[vindex]]
                let grftype;
                switch (userParams.variables[vindex]) {
                    case 'pcpn':
                    case 'tsky':
                    case 'vis':
                        grftype = "column"
                        break;
                    case 'wdir':
                        grftype = "scatter"
                        break;
                    default:
                        grftype = "line"
                }
                series.push({
                    data: tsarrs[vindex],
                    name:  v.name + " (" + v.units + ")",
                    color: v.color ? v.color : "",
                    type: grftype,
                    boostThreshold: grftype === 'scatter' && tsarrs[vindex].length > 8784 ? 0 : 8784,
                    yAxis: userParams.variables[vindex] === 'wdir' && garr.length > 1 ? 1 : 0,
                })
            })

            // save HighCharts options
            highChartOptions.push({
                series: series, 
                plotOptions: {
                    series: {
                        pointStart: ptst,
                        pointInterval: 36e5, // one hour
                        marker: {
                            radius: 2,
                        },
                    },
                },
                yAxis: [
                    {
                        title: {
                            text: undefined
                        },
                        endOnTick: (g === 2 || g === 4) ? false : true,
                    }, {
                        //only used for wind direction
                        title: {
                            text: undefined
                        },
                        opposite: true,
                        showLastLabel: false,
                        softMax: 360,
                    }
                ]
            })
        }
    })
    return highChartOptions;
}

const processData = (res, userParams) => {
	var rval;
	const noVal = 'na';  //code for when wind chill or heat index input outside range
	//initializations
	let sumObs = 0;
	let sumMiss = 0;
    const tsarrs =  Array.from({ length: res.data[0].length - 1 }, () => []);

	//process
	res.data.forEach((results, i) => {
        results.forEach((value, v) => {
            if (v === 0) { return } //date-time
            if (userParams.variables[v-1] === "pcpn" && value === "T") { 
                rval = traceValue 
            } else if (userParams.variables[v-1] === "wdir" && value === "CLM") { 
                rval = clmValue
            } else if (userParams.variables[v-1] === "wdir" && value === "VRB") { 
                rval = vrbValue
            } else if (userParams.variables[v-1] === "tsky" && value !== "M") { 
                rval = tsky(value, obsValue) * 100
            } else {
                rval = ["M",noVal].includes(value) ? "M" : parseFloat(value);
            }
            if (rval !== "M" ) {
                tsarrs[v-1].push(rval)
                sumObs += 1
            } else {
                tsarrs[v-1].push(null)
                sumMiss += 1
            }
        })
 	})

    // set up options for HighCharts
    const highChartOptions = summarize(tsarrs, res.data[0][0], userParams)
	return {highChartOptions, sumObs, sumMiss}
}

export function processMeteogrf(userParams, setResult, hourlyVx) {
    const sid = userParams.sid;
    const network = sid.indexOf(" ") > 0 ? sid.split(" ")[1] : 'icao';
    const starttime = userParams.fromDate.hour(1)
    const sdate = [starttime.year(),starttime.month()+1,starttime.date(),starttime.hour()] || [2025,1,1,1];
    // end hour 24, which is hour 0 the next day
    const endtime = userParams.toDate.add(1, 'days').hour(0);
    const edate = [endtime.year(),endtime.month()+1,endtime.date(),starttime.hour()] || [2025,1,2,0];
    const meta = ["name","state"]
    const elems = [];
    userParams.variables.forEach((v) => {
        if (v === 'tsky') {
			elems.push({vX: hourlyVx[network]['clds'], interval: [0,0,0,1], duration: 1})
        } else {
            elems.push({vX: hourlyVx[network][v], interval: [0,0,0,1], duration: 1})
        }
    })

   if (userParams.variables.includes("wdir")) {
    setResult("Processing request" + (endtime.diff(starttime, 'hour') > 8784 ? "; inclusion of wind direction can slow rendering and result in poor chart interactivity..." : "..."));
   } else {
    setResult("Processing request" + (endtime.diff(starttime, 'year') >= 5 ? "; long date ranges may result in slow rendering and poor chart interactivity..." : "..."));
   }

    const acisParams = {
        sid: sid,
        sDate: sdate,
        eDate: edate,
        elems: elems,
        meta: meta,
    }
    getAcisData(acisParams)
    .then(res => {
        const results = processData(res, userParams);
        const title = "Hourly data for " + res.meta.name + ", " + res.meta.state;
        sendResults(results, title, setResult)
    })
    .catch(error =>  { 
        console.log("Data retrieval error: "); 
        console.log(error); 
        setResult((
            <Typography sx={{color: "red"}}>
                Error processing request
            </Typography>
        ))
    })

}